import React, { useState, useEffect } from 'react';
import { createRequestCategories, REQUEST_CATEGORY_TYPES } from '../../services/requestService';
import { SuccessCircle, WarningIcon } from '../../assets/icons';
import Spinner from '../common/Spinner';
import useModalClose from '../../hooks/useModalClose';

const STEPS = {
  FORM: 0,
  CONFIRM: 1,
  COMPLETE: 2
};



const INITIAL_FORM_STATE = {
  adi: '',
  sira: '',
  aciklama: '',
  tur: ''
};

const CreateRequestModal = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [createdUser, setCreatedUser] = useState(null);

  // Reset all state when modal closes
  const resetState = () => {
    setCurrentStep(STEPS.FORM);
    setIsLoading(false);
    setError(null);
    setFormData(INITIAL_FORM_STATE);
    setCreatedUser(null);
  };

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  // Add modal closing behaviors
  useModalClose(isOpen, onClose);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
     
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    
  };

  const validateForm = () => {
    if (!formData.adi) {
      setError('Lütfen adı alanları doldurunuz.');
      return false;
    }
    if (!formData.sira) {
      setError('Lütfen sıra  doldurunuz.');
      return false;
    }
    if (!formData.tur) {
      setError('Lütfen tür seçiniz.');
      return false;
    }
   
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    if (validateForm()) {
      setCurrentStep(STEPS.CONFIRM);
    }
  };

  const handleBack = () => {
    setCurrentStep(STEPS.FORM);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    setCurrentStep(STEPS.COMPLETE);

    try {
      const response = await createRequestCategories({
        ...formData,
        isActive: true
      });
      setCreatedUser(response.data);
      onSuccess(response.data);
    } catch (error) {
      setError('Kategori oluşturulurken bir hata oluştu.');
      console.error('Grup  error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="stepper">
      <div className={`step ${currentStep >= STEPS.FORM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.FORM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Kategori Bilgileri</span>
      </div>
      <div className={`step ${currentStep >= STEPS.CONFIRM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.CONFIRM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Bilgi Onayı</span>
      </div>
      <div className={`step ${currentStep >= STEPS.COMPLETE ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.COMPLETE && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Tamamlandı</span>
      </div>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="create-user-form">
      <div className="form-group">
        <label htmlFor="adi">Kategori Adı</label>
        <input
          type="text"
          id="adi"
          name="adi"
          value={formData.adi}
          onChange={handleInputChange}
          placeholder="Ad"
        />
      </div>
      <div className="form-group">
        <label htmlFor="sira">Kategori Sırası</label>
        <input
          type="number"
          id="sira"
          name="sira"
          value={formData.sira}
          onChange={handleInputChange}
          placeholder="Sira"
        />
      </div>
      <div className="form-group">
        <label htmlFor="tur">Tür</label>
        <select id="tur" name="tur" value={formData.tur} onChange={handleInputChange}>
          <option value="">Seçiniz</option>
          {REQUEST_CATEGORY_TYPES.map((tur) => (
            <option key={tur} value={tur}>{tur}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="aciklama">Açıklama</label>
        <textarea
          id="aciklama"
          name="aciklama"
          value={formData.aciklama}
          onChange={handleInputChange}
          placeholder="Açıklama"
          rows={3}
        />
      </div>
      {error && <div className="error-message">{error}</div>}
      <div className="form-actions">
        <button type="submit" className="submit-button">
          Ekle
        </button>
      </div>
    </form>
  );

  const renderConfirmation = () => (
    <div className="confirmation-step">
      <WarningIcon />
      <h3>Sisteme yeni bir kaytegori kaydetmek üzeresiniz. Onaylıyor musunuz?</h3>
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Kategori Adı</th>
              <th>Tür</th>
              <th>Açıklama</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{`${formData.adi}`}</td>
              <td>{formData.tur}</td>
              <td>{formData.aciklama || '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-actions">
        <button type="button" onClick={handleBack} className="back-button">
          İptal Et
        </button>
        <button type="button" onClick={handleConfirm} className="confirm-button">
          Sonraki Adım
        </button>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="complete-step">
      {isLoading ? (
        <div className="loading-state">
          <Spinner size="large" />
          <h3>Süreciniz tamamlanıyor...</h3>
          <button type="button" onClick={handleClose} className="cancel-button">
            Süreci İptal Et
          </button>
        </div>
      ) : createdUser ? (
        <div className="success-state">
          <SuccessCircle />
          <h3>Süreciniz tamamlandı. Aşağıdaki Kategori sisteme eklendi.</h3>
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Kategori adı</th>
                  <th>Tür</th>
                  <th>Açıklama</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{`${createdUser.adi}`}</td>
                  <td>{createdUser.tur || '-'}</td>
                  <td>{createdUser.aciklama || '-'}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" onClick={handleClose} className="close-button">
            Kapat
          </button>
        </div>
      ) : (
        <div className="error-state">
          <div className="error-icon">✕</div>
          <h3>Hata Oluştu</h3>
          <p>{error}</p>
          <button type="button" onClick={handleBack} className="retry-button">
            Tekrar Dene
          </button>
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Yeni Kategori Ekle</h3>
        </div>
        {renderStepIndicator()}
        <div className="modal-body">
          {currentStep === STEPS.FORM && renderForm()}
          {currentStep === STEPS.CONFIRM && renderConfirmation()}
          {currentStep === STEPS.COMPLETE && renderComplete()}
        </div>
      </div>
    </div>
  );
};

export default CreateRequestModal; 